import React, { useState, useEffect, useRef, useCallback } from "react";

const SearchableSelect = ({
  options = [],
  value = "",
  onChange,
  onBlur,
  name = "",
  placeholder = "Search and select...",
  getOptionLabel = (option) => option.name || option.label || "",
  getOptionValue = (option) => option._id || option.value || option.id,
  searchFields = ["name", "number", "_id", "label"],
  disabled = false,
  className = "",
  error = false,
  required = false,
  showSearchIcon = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({
    top: 0,
    left: 0,
    width: "100%",
    maxHeight: "280px",
  });

  // Filter options based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredOptions(options);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = options.filter((option) => {
      // Search in all specified fields
      return searchFields.some((field) => {
        const fieldValue = option[field];
        if (fieldValue === undefined || fieldValue === null) return false;
        return String(fieldValue).toLowerCase().includes(query);
      });
    });

    setFilteredOptions(filtered);
    setHighlightedIndex(-1);
  }, [searchQuery, options, searchFields]);

  // Get selected option label
  const selectedOption = options.find((opt) => getOptionValue(opt) === value);
  const displayValue = selectedOption ? getOptionLabel(selectedOption) : "";

  // Keep searchQuery in sync with selected value when closed
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery(displayValue || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayValue, isOpen]);

  const updateDropdownPosition = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const gap = 6;
    const minHeight = 140;
    const defaultMaxHeight = 280;
    const spaceBelow = viewportHeight - rect.bottom - gap - 12;
    const maxHeight = Math.max(minHeight, Math.min(defaultMaxHeight, spaceBelow));
    setDropdownStyle({
      top: rect.bottom + gap,
      left: rect.left,
      width: `${rect.width}px`,
      maxHeight: `${maxHeight}px`,
    });
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedOutsideInput =
        containerRef.current && !containerRef.current.contains(event.target);
      const clickedOutsideDropdown =
        dropdownRef.current && !dropdownRef.current.contains(event.target);
      if (clickedOutsideInput && clickedOutsideDropdown) {
        setIsOpen(false);
        setSearchQuery("");
        if (onBlur) {
          onBlur();
        }
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", updateDropdownPosition, true);
      window.addEventListener("resize", updateDropdownPosition);
      updateDropdownPosition();
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener("scroll", updateDropdownPosition, true);
        window.removeEventListener("resize", updateDropdownPosition);
      };
    }
  }, [isOpen, onBlur, updateDropdownPosition]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.children[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
      }
    }
  }, [highlightedIndex]);

  // Handle input focus
  const handleInputFocus = () => {
    updateDropdownPosition();
    setIsOpen(true);
    setSearchQuery(displayValue || "");
  };

  // Handle option select
  const handleSelect = (option) => {
    const optionValue = getOptionValue(option);
    onChange({
      target: {
        name: name || containerRef.current?.querySelector("input")?.name || "",
        value: optionValue,
      },
    });
    setIsOpen(false);
    setSearchQuery("");
    setHighlightedIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "Enter" || e.key === "ArrowDown") {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setSearchQuery("");
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <style>{`
        .searchable-select-container {
          position: relative;
          width: 100%;
        }

        .searchable-select-input-wrapper {
          position: relative;
          width: 100%;
        }

        .searchable-select-input {
          width: 100%;
          padding: 8px 35px 8px 12px;
          border: 2px solid #e1e5e9;
          border-radius: 6px;
          font-size: 0.95rem;
          cursor: pointer;
          background: white;
          transition: all 0.3s ease;
        }

        .searchable-select-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .searchable-select-input.error {
          border-color: #dc3545;
        }

        .searchable-select-input:disabled {
          background: #f5f5f5;
          cursor: not-allowed;
          opacity: 0.7;
        }

        .searchable-select-icon {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          pointer-events: none;
          color: #666;
          font-size: 1.2rem;
        }

        .searchable-select-dropdown {
          position: fixed;
          background: white;
          border: 2px solid #e1e5e9;
          border-radius: 6px;
          overflow-y: auto;
          overflow-x: hidden;
          z-index: 10000;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          -webkit-overflow-scrolling: touch;
        }

        .searchable-select-dropdown::-webkit-scrollbar {
          width: 8px;
        }

        .searchable-select-dropdown::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }

        .searchable-select-dropdown::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 4px;
        }

        .searchable-select-dropdown::-webkit-scrollbar-thumb:hover {
          background: #555;
        }

        .searchable-select-search {
          padding: 6px 12px;
          border-bottom: 1px solid #e1e5e9;
          position: sticky;
          top: 0;
          background: white;
          z-index: 1;
        }

        .searchable-select-search-input {
          width: 100%;
          padding: 6px 10px;
          border: 1px solid #e1e5e9;
          border-radius: 4px;
          font-size: 0.9rem;
        }

        .searchable-select-search-input:focus {
          outline: none;
          border-color: #667eea;
        }

        .searchable-select-options {
          padding: 2px 0;
          min-width: 100%;
        }

        .searchable-select-option {
          padding: 6px 12px;
          cursor: pointer;
          transition: background 0.2s ease;
          border: none;
          width: 100%;
          text-align: left;
          background: none;
          font-size: 0.95rem;
          line-height: 1.4;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .searchable-select-option:hover,
        .searchable-select-option.highlighted {
          background: #f0f4ff;
        }

        .searchable-select-option.selected {
          background: #e7f3ff;
          font-weight: 600;
          color: #0056b3;
        }

        .searchable-select-no-results {
          padding: 8px 12px;
          text-align: center;
          color: #666;
          font-size: 0.9rem;
        }

        .searchable-select-empty {
          padding: 8px 12px;
          text-align: center;
          color: #999;
          font-size: 0.9rem;
        }

        @media (max-width: 768px) {
          .searchable-select-input {
            padding: 6px 30px 6px 10px;
            font-size: 0.85rem;
          }

          .searchable-select-dropdown {
            /* max-height is set dynamically via inline style */
          }

          .searchable-select-option {
            padding: 8px 10px;
            font-size: 0.85rem;
          }
        }
      `}</style>

      <div
        className={`searchable-select-container ${className}`}
        ref={containerRef}
      >
        <div className="searchable-select-input-wrapper">
          <input
            ref={inputRef}
            type="text"
            name={name}
            value={searchQuery}
            onChange={(e) => {
              setIsOpen(true);
              setSearchQuery(e.target.value);
            }}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={`searchable-select-input ${error ? "error" : ""}`}
            disabled={disabled}
            required={required}
          />
          {showSearchIcon && (
            <span className="searchable-select-icon">
              {isOpen ? "🔍" : "▼"}
            </span>
          )}
        </div>

        {isOpen && (
          <div
            className="searchable-select-dropdown"
            ref={dropdownRef}
            style={dropdownStyle}
          >
            <div className="searchable-select-options">
              {filteredOptions.length === 0 ? (
                <div className="searchable-select-no-results">
                  {searchQuery
                    ? `No results found for "${searchQuery}"`
                    : "No options available"}
                </div>
              ) : (
                filteredOptions.map((option, index) => {
                  const optionValue = getOptionValue(option);
                  const optionLabel = getOptionLabel(option);
                  const isSelected = optionValue === value;
                  const isHighlighted = index === highlightedIndex;

                  return (
                    <button
                      key={optionValue}
                      type="button"
                      className={`searchable-select-option ${
                        isSelected ? "selected" : ""
                      } ${isHighlighted ? "highlighted" : ""}`}
                      onClick={() => handleSelect(option)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                    >
                      {optionLabel}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SearchableSelect;

